import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Shield, Users, Building2, Settings, FileText, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import AdminUsers from './AdminUsers';
import AdminWorkspaces from './AdminWorkspaces';
import AdminWorkspaceDetail from './AdminWorkspaceDetail';
import AdminOverrides from './AdminOverrides';
import AdminAuditLogs from './AdminAuditLogs';

const AdminSupport = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is admin
    if (user) {
      setIsAdmin(user.is_admin === true);
      setLoading(false);
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Checking admin access...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <Shield className="h-16 w-16 text-destructive" />
        <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
        <p className="text-muted-foreground">You do not have admin privileges to access this page.</p>
        <Button onClick={() => navigate('/')} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back Home
        </Button>
      </div>
    );
  }

  const navItems = [
    { path: '/admin/support/users', label: 'Users', icon: Users },
    { path: '/admin/support/workspaces', label: 'Workspaces', icon: Building2 },
    { path: '/admin/support/overrides', label: 'Overrides', icon: Settings },
    { path: '/admin/support/audit-logs', label: 'Audit Logs', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                <h1 className="text-xl font-bold text-foreground">Admin Support</h1>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {user?.email}
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 border-r border-border bg-card min-h-[calc(100vh-4rem)]">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Routes>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:userId" element={<AdminUsers />} />
            <Route path="workspaces" element={<AdminWorkspaces />} />
            <Route path="workspaces/:workspaceId" element={<AdminWorkspaceDetail />} />
            <Route path="overrides" element={<AdminOverrides />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminSupport;
